import { useLayoutEffect, useRef, useState } from 'react';

import { CHARTV2_HORIZON_SPAN_MS } from '../constants';
import { createXScale, DEFAULT_CANVAS_PADDING } from '../engine/scales';
import type { ChartV2Insight } from '../types/chart-v2.types';
import styles from './InsightsOverlay.module.scss';

/**
 * v1 counts calendar months between the timeseries points at the domain
 * bounds, and its 6M horizon starts exactly six calendar months back, so
 * 6M always measures as six. ChartV2 horizons are fixed day spans, where
 * a 182-day window crossing February straddles seven calendar boundaries
 * — comparing spans keeps the 6M horizon on the "show insights" side.
 */
const MAX_INSIGHTS_DOMAIN_MS = CHARTV2_HORIZON_SPAN_MS['6M'] as number;

export interface InsightsOverlayProps {
  insights: ChartV2Insight[];
  domain: [number, number];
  /**
   * Number of reserved y-axis columns — must match the value the canvas
   * uses (`ChartV2`'s shared `axisColumnCount`, which already accounts for
   * `indexed` price mode) so insight positions line up with the plot area.
   */
  axisColumnCount: number;
  /**
   * Width (px) of each reserved y-axis column. Shared with the canvas so
   * insight x-positions line up with the plot area.
   */
  axisColumnWidth: number;
  /**
   * Ordered bar timestamps shared with the canvas (`buildBarTimeline` on
   * the pane's full series list, including hidden). Must match so hide
   * keeps insights visible and x-aligned — v1 parity.
   */
  barTimeline?: number[];
  /**
   * Whether this pane owns the x-axis (a little more bottom padding is
   * reserved for the tick labels when true). Also gates the zoom-in
   * label: exactly one pane owns the axis, so this keeps the label to one
   * per chart like v1, which renders it for the whole canvas.
   */
  isXAxisPane?: boolean;
  zoomInToViewText?: string;
  /**
   * Loaded data span. The zoom-in check measures the domain clipped to
   * this — v1 reads timestamps out of the timeseries by domain index, so
   * the future pad `computeInitialDomain` adds never counts towards the
   * 6-month limit (a padded 6M domain otherwise reads as 7 months).
   */
  dataExtent?: { start: number; end: number };
}

const PLOT_BOTTOM_GAP = 2;

export const InsightsOverlay = ({
  insights,
  domain,
  axisColumnCount,
  axisColumnWidth,
  barTimeline,
  zoomInToViewText,
  dataExtent,
  isXAxisPane = false,
}: InsightsOverlayProps) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);

  useLayoutEffect(() => {
    const node = rootRef.current;

    if (!node) {
      return undefined;
    }

    const observer = new ResizeObserver(entries => {
      const entry = entries[0];

      if (entry) {
        setWidth(entry.contentRect.width);
      }
    });

    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const measuredStart = Math.max(domain[0], dataExtent?.start ?? domain[0]);
  const measuredEnd = Math.min(domain[1], dataExtent?.end ?? domain[1]);
  const domainExceedsMaxWindow =
    measuredEnd - measuredStart > MAX_INSIGHTS_DOMAIN_MS;

  const plotLeft = DEFAULT_CANVAS_PADDING.left;
  const plotRight =
    width - DEFAULT_CANVAS_PADDING.right - axisColumnCount * axisColumnWidth;
  const plotWidth = Math.max(0, plotRight - plotLeft);
  const xScale = createXScale({
    xDomain: domain,
    plotX: plotLeft,
    plotWidth,
    barTimeline,
  });
  const visible = domainExceedsMaxWindow
    ? []
    : insights.filter(i => i.time >= domain[0] && i.time <= domain[1]);
  const bottom = isXAxisPane
    ? DEFAULT_CANVAS_PADDING.bottom + PLOT_BOTTOM_GAP
    : PLOT_BOTTOM_GAP;

  return (
    <div
      ref={rootRef}
      className={styles.root}
      data-testid="chartv2-insights-overlay"
    >
      {domainExceedsMaxWindow && !!zoomInToViewText && isXAxisPane && (
        <p
          className={styles.zoomIntoViewText}
          style={{ left: plotLeft + plotWidth / 2, bottom }}
          data-testid="chartv2-zoom-in-required"
        >
          {zoomInToViewText}
        </p>
      )}
      {plotWidth > 0 &&
        visible.map(insight => {
          const xPx = xScale(insight.time);

          return (
            <div
              key={insight.id}
              className={styles.insight}
              style={{ left: xPx, bottom }}
              data-testid={`chartv2-insight-${insight.id}`}
            >
              {insight.icons.map((icon, index) => (
                <div
                  key={index}
                  className={styles.icon}
                  data-testid={`chartv2-insight-${insight.id}-icon-${index}`}
                >
                  {icon}
                </div>
              ))}
            </div>
          );
        })}
    </div>
  );
};
